/* eslint-disable @typescript-eslint/camelcase */
/* eslint-disable indent */
import {
  FormControlLabel,
  Grid,
  makeStyles,
  Radio,
  RadioGroup,
  Typography,
} from '@material-ui/core';
import {
  ExpandMore as ExpandMoreIcon,
  ThumbUp as ThumbUpIcon,
  Visibility as ViewIcon,
} from '@material-ui/icons';
import clsx from 'clsx';
import Button from 'components/Button';
import Loading from 'components/Loading';
import { SearchBar } from 'components/SearchBar';
import TableItems, { TableCell, TableRow } from 'components/TableItems';
import { Formik } from 'formik';
import { useTableFilters } from 'hooks/useTableFilters';
import i18n from 'i18n';
import { CompanyData, RolesEnum } from 'modules/authenticate/interfaces';
import {
  clearTestOpenApplication,
  getAllClientsRequest,
  saveChromeExtensionRequest,
  saveUrlConnectionRequest,
  testOpenApplicationRequest,
} from 'modules/clients/actions';
import {
  ClientFields,
  ListClientDataProps,
  TestConnectionProps,
  TestConnectionResponse,
} from 'modules/clients/interfaces';
import { InstanceFields } from 'modules/environments/interfaces';
import { messagesRequest } from 'modules/messages/actions';
import { modalClose } from 'modules/modal/actions';
import React from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import GenericForm from 'skyone-lib/lib/GenericForm';
import ApplicationState from 'store/interfaces';
import { inAdminView, inOperatorView, inSuperAdminView } from 'utils/role';
import screenFields from './formFields';
import { initialValues } from './initialValues';
import validationSchema from './schema';
import styles from './styles';
import { useSimulateApp } from './useSimulateApp';

const useStyles = makeStyles(styles);

interface Props {
  client?: ClientFields | null;
  clientList: ListClientDataProps;
  companySelected: CompanyData | null;
  instance?: InstanceFields;
  isConnecting: boolean;
  isLoading: boolean;
  role?: RolesEnum;
  testConnection: TestConnectionResponse | null;
  getAllClientsRequest: (props) => void;
  modalClose: () => void;
  testOpenApplicationRequest: (props) => void;
  saveUrlConnectionRequest: () => void;
  saveChromeExtensionRequest: () => void;
  clearTestOpenApplication: () => void;
  messagesRequest: (props) => void;
}

const initialQuery = {
  search: '',
  limit: 10,
  offset: 0,
};

const FormSimulateApp = ({
  client,
  clientList,
  companySelected,
  isConnecting,
  isLoading,
  instance,
  role,
  testConnection,
  getAllClientsRequest,
  modalClose,
  testOpenApplicationRequest,
  saveUrlConnectionRequest,
  saveChromeExtensionRequest,
  clearTestOpenApplication,
  messagesRequest,
}: Props) => {
  const classes = useStyles();
  const { id } = useParams<{ id: string }>();
  const {
    openInBrowserUrl,
    openRemote,
    handleOpenInBrowser,
    handleOpenRemote,
  } = useSimulateApp({
    testConnection,
    saveUrlConnectionRequest,
    saveChromeExtensionRequest,
    clearTestOpenApplication,
    messagesRequest,
  });
  const [useTemporaryUser, setUseTemporaryUser] = React.useState(true);
  const [clientFields, setClientFields] = React.useState({
    client_name: client?.name,
    client_uuid: client?.client_uuid,
  });
  const [openClientSelector, setOpenClientSelector] = React.useState(true);

  const {
    query,
    handleLimitChange,
    handleOrder,
    handlePageChange,
    handleSearch,
    setSearchValue,
  } = useTableFilters(initialQuery);

  React.useEffect(() => {
    if (!client) {
      getAllClientsRequest({
        ...query,
        environment_uuid: id,
      });
    }
  }, [query, getAllClientsRequest, id, client]);

  const handleSubmit = (values: TestConnectionProps) => {
    if (!clientFields?.client_uuid) return;

    let data: {
      id: string;
      values?: TestConnectionProps;
      instanceUuid?: string;
    } = {
      id: clientFields.client_uuid,
    };

    if (!useTemporaryUser) {
      data = {
        ...data,
        values,
      };
    }

    if (instance?.instance_uuid) {
      data = {
        ...data,
        instanceUuid: instance.instance_uuid,
      };
    }

    testOpenApplicationRequest(data);
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      validateOnChange
      validateOnMount
    >
      {({ values, setFieldValue, errors, isValid }) => (
        <Grid spacing={2} container>
          <Grid container className={classes.container}>
            <Typography className={classes.text}>
              {`${i18n.t('APPLICATIONS_PAGE.SIMULATE_APP_EXPLANATION_KEY')} ${
                !client
                  ? i18n.t(
                      'APPLICATIONS_PAGE.SELECT_CLIENT_TO_SIMULATE_APP_KEY'
                    )
                  : ''
              }`}
            </Typography>

            <Grid
              container
              className={clsx(classes.titleGrid, {
                [classes.titleGridError]: !clientFields?.client_uuid,
              })}
            >
              <Grid container alignItems="center">
                <Typography className={classes.text}>{`${i18n.t(
                  'CLIENT_KEY'
                )}:`}</Typography>
                <Grid item>
                  <Button
                    id="btn-select-client"
                    onClick={() => setOpenClientSelector(!openClientSelector)}
                    endIcon={!client && <ExpandMoreIcon />}
                    disabled={!!client}
                  >
                    {clientFields?.client_name ? (
                      <Typography className={classes.boldText}>
                        {clientFields?.client_name}
                      </Typography>
                    ) : (
                      <Typography className={classes.boldText}>
                        {i18n.t('CHOOSE_CLIENT_KEY')}
                      </Typography>
                    )}
                  </Button>
                </Grid>
              </Grid>
              {!client && openClientSelector && (
                <Grid className={classes.clientsTable}>
                  <SearchBar
                    id="search-clients"
                    isLoading={isLoading}
                    handleSearch={handleSearch}
                    setSearchValue={setSearchValue}
                  />
                  <TableItems
                    header={
                      <>
                        <TableCell>
                          <></>
                        </TableCell>
                        <TableCell
                          key="client_name"
                          order
                          handleOrder={() => handleOrder('client_name')}
                        >
                          {i18n.t('CLIENT_NAME_KEY')}
                        </TableCell>
                      </>
                    }
                    renderItems={(item: ClientFields) => (
                      <TableRow
                        key={item?.client_uuid}
                        onClick={() => {
                          setClientFields({
                            client_name: item?.name,
                            client_uuid: item?.client_uuid,
                          });
                          setOpenClientSelector(false);
                        }}
                        className={clsx(classes.tableRow, {
                          [classes.clientSelected]:
                            clientFields?.client_uuid === item?.client_uuid,
                        })}
                      >
                        <TableCell className={classes.likeCell}>
                          {clientFields?.client_uuid === item?.client_uuid ? (
                            <ThumbUpIcon fontSize="small" />
                          ) : (
                            <></>
                          )}
                        </TableCell>
                        <TableCell className={classes.clientCell}>
                          <Typography>{item?.name}</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    data={clientList}
                    isLoading={isLoading}
                    onChangePage={handlePageChange}
                    onChangeLimit={handleLimitChange}
                    overrideRowsCount={query.limit}
                    notFoundText={i18n.t('CLIENTS_PAGE.NO_CLIENTS_KEY')}
                  />
                </Grid>
              )}
            </Grid>

            <Grid container justify="center">
              <RadioGroup
                className={classes.radioGroup}
                value={useTemporaryUser}
                onChange={(_, value) =>
                  value === 'false'
                    ? setUseTemporaryUser(false)
                    : setUseTemporaryUser(true)
                }
              >
                <FormControlLabel
                  value={true}
                  control={<Radio />}
                  label={i18n.t('APPLICATIONS_PAGE.USE_TEMPORARY_USER_KEY')}
                />
                <FormControlLabel
                  value={false}
                  control={<Radio />}
                  label={i18n.t('APPLICATIONS_PAGE.USE_EXISTING_USER_KEY')}
                />
              </RadioGroup>
            </Grid>

            {!useTemporaryUser && (
              <GenericForm
                fields={values}
                setFieldValue={setFieldValue}
                errors={errors}
                screenFields={screenFields}
              />
            )}

            {(openInBrowserUrl || openRemote) && (
              <Grid item className={classes.testButtons}>
                {openInBrowserUrl && (
                  <Button
                    variant="contained"
                    size="medium"
                    color="secondary"
                    className={clsx({
                      [classes.addButtonSuperAdmin]: inSuperAdminView(
                        role,
                        companySelected
                      ),
                      [classes.addButtonAdmin]: inAdminView(
                        role,
                        companySelected
                      ),
                      [classes.addButtonOperator]: inOperatorView(
                        role,
                        companySelected
                      ),
                      [classes.addButtonCompanyContext]: companySelected,
                    })}
                    startIcon={<ViewIcon className={classes.viewIcon} />}
                    onClick={handleOpenInBrowser}
                  >
                    {i18n.t('APPLICATIONS_PAGE.OPEN_APPLICATION_KEY')}
                  </Button>
                )}

                {openRemote && (
                  <Button
                    variant="contained"
                    id="open-remote-app"
                    size="medium"
                    className={clsx({
                      [classes.addButtonSuperAdmin]: inSuperAdminView(
                        role,
                        companySelected
                      ),
                      [classes.addButtonAdmin]: inAdminView(
                        role,
                        companySelected
                      ),
                      [classes.addButtonOperator]: inOperatorView(
                        role,
                        companySelected
                      ),
                      [classes.addButtonCompanyContext]: companySelected,
                    })}
                    color="secondary"
                    startIcon={<ViewIcon className={classes.viewIcon} />}
                    onClick={handleOpenRemote}
                  >
                    {i18n.t(
                      'APPLICATIONS_PAGE.OPEN_APPLICATION_WITH_REMOTE_APP_KEY'
                    )}
                  </Button>
                )}
              </Grid>
            )}
          </Grid>

          <Grid container justify="flex-end" className={classes.buttonsBase}>
            <Grid container className={classes.buttonsAlign}>
              <Button
                submitButton
                id="submit-button"
                onClick={() => handleSubmit(values)}
                disabled={
                  isConnecting ||
                  !clientFields?.client_uuid ||
                  (!isValid && !useTemporaryUser)
                }
              >
                {isConnecting ? <Loading size={15} /> : i18n.t('CONNECT_KEY')}
              </Button>
              <Button
                id="cancel-button"
                variant="outlined"
                onClick={modalClose}
              >
                {i18n.t('CANCEL_KEY')}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Formik>
  );
};

const mapStateToProps = ({
  clients: { isConnecting, clientList, isLoading, testConnection },
  authenticate: { companySelected, data },
}: ApplicationState) => ({
  clientList,
  companySelected,
  isConnecting,
  isLoading,
  role: data?.role,
  testConnection,
});

export default connect(mapStateToProps, {
  getAllClientsRequest,
  modalClose,
  testOpenApplicationRequest,
  saveUrlConnectionRequest,
  saveChromeExtensionRequest,
  clearTestOpenApplication,
  messagesRequest,
})(FormSimulateApp);
